
import { computed, watch, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'

// types
import { ConfigItem } from '@/core/types/contracts.types'

// realisations
import { contracts } from '@/core/realisations/contracts.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

const COLUMNS = [
    {
        name: 'Ключ',
        key: 'key'
    },
    {
        name: 'Значение',
        key: 'value'
    },
    {
        name: '',
        key: 'actions'
    }
]

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'failed-fetch': FailedFetch,
        TheTable,
        TableScrollWrapper,
        Modal,
    },
    setup() {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            isLoading: false,
            isFailed: false,
            items: [] as ConfigItem[],
            //
            page: 1,
            limit: 10,
            totalPages: 1,
            totalItems: 1,
            //
            isUpdateModalShowed: false,
            isUpdateModalLoading: false,
            updateModalItem: null as ConfigItem | null,
            updateModalItemValues: [] as string[],
            updateModalItemValidations: [] as string[]
        })

        const loadConfig = async (page: number) => {
            state.page = page

            try {
                state.isLoading = true

                const response = await contracts.getConfigList({
                    page: state.page,
                    limit: state.limit
                })

                state.items = response.data
                state.totalPages = response.meta.last_page
                state.totalItems = response.meta.total

                state.isFailed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })

                state.isFailed = true
            } finally {
                state.isLoading = false
            }
        }

        onBeforeMount(() => { loadConfig(1) })

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadConfig(state.page)
        }

        const onStartUpdateItem = (item: ConfigItem) => {
            state.updateModalItem = { ...item }
            state.updateModalItemValues = [...item.value]
            state.updateModalItemValidations = Array.from({ length: item.value.length }, () => '')
            state.isUpdateModalShowed = true
        }

        const onCancelUpdateItem = () => {
            state.isUpdateModalShowed = false
            state.updateModalItem = null
            state.updateModalItemValues = []
            state.updateModalItemValidations = []
        }

        const onUpdateModalItemValue = (value: string, index: number) => {
            if (state.isUpdateModalLoading) return

            state.updateModalItemValues[index] = value
            validateModalItems()
        }

        const onDeleteModalItem = (index: number) => {
            if (state.isUpdateModalLoading) return

            state.updateModalItemValues.splice(index, 1)
            state.updateModalItemValidations.splice(index, 1)
        }

        const onAddModalItem = () => {
            if (state.isUpdateModalLoading) return

            state.updateModalItemValues.push('')
            state.updateModalItemValidations.push('')
        }

        const updateItem = async () => {
            if (state.isUpdateModalLoading || !state.updateModalItem || !isCanUpdate.value) return

            if (state.updateModalItem.key === 'dogovor24_allowed_signer_emails' && state.updateModalItemValues.length == 0) {
                const answer = confirm('Сменить подписанта никто не сможет, продолжить?')
                if (!answer) return
            }

            try {
                state.isUpdateModalLoading = true

                await contracts.updateConfigKey(
                    state.updateModalItem.key,
                    state.updateModalItemValues.filter(Boolean).map((v) => v.trim().toLowerCase())
                )

                notifications.notify({ type: 'success', title: 'Запись отредактироана' })
                onCancelUpdateItem()

                loadConfig(state.page)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При редактировании произошла ошибка' })
            } finally {
                state.isUpdateModalLoading = false
            }
        }

        const isValidEmail = (email: string): boolean => {
            const trimmed = email.trim()
            if (!trimmed) return false

            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return re.test(trimmed)
        }

        const isDuplicate = (value: string, index: number): boolean => {
            const trimmed = value.trim().toLowerCase()
            if (!trimmed) return false

            return state.updateModalItemValues.some((item, i) => i !== index && item.trim().toLowerCase() === trimmed)
        }

        const validateValue = (value: string, index: number) => {
            if (state.updateModalItem) {
                if (state.updateModalItem.key === 'dogovor24_allowed_signer_emails') {
                    if (isDuplicate(value, index)) {
                        state.updateModalItemValidations[index] = 'Повторяющееся значение'
                    } else if (!isValidEmail(value)) {
                        state.updateModalItemValidations[index] = 'Невалидный email'
                    } else {
                        state.updateModalItemValidations[index] = ''
                    }
                } else {
                    if (isDuplicate(value, index)) {
                        state.updateModalItemValidations[index] = 'Повторяющееся значение'
                    }else {
                        state.updateModalItemValidations[index] = ''
                    }
                }
            }
        }

        const validateModalItems = () => {
            state.updateModalItemValues.forEach((v, i) => validateValue(v, i))
        }

        const isModalInvalid = computed(() => state.updateModalItemValidations.filter(Boolean).length > 0)
        const isCanUpdate = computed(() => havePermissions(['contract-config-update', 'all-root-permission']))

        watch(() => state.updateModalItemValues.length, validateModalItems)

        return {
            COLUMNS,
            state,
            loadConfig,
            onLimitChange,
            onStartUpdateItem,
            onCancelUpdateItem,
            onUpdateModalItemValue,
            onDeleteModalItem,
            onAddModalItem,
            updateItem,
            isModalInvalid,
            isCanUpdate
        }
    }
})
